import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores/ui';
import { useInstructionsStore } from '@/stores/instructions';
import { useTasksStore } from '@/stores/tasks';
import { useProposalsStore } from '@/stores/proposals';
import { useValidatorsStore } from '@/stores/validators';
import { useContextBundlesStore } from '@/stores/contextBundles';
import { Proposal } from '@/core/state-shape';
import type { ValidatorResults } from '@/core/state-shape';
import { http } from '@/lib/http';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from '@/components/ui/separator';
import { AlertCircle, CheckCircle, RotateCcw, Milestone, ToyBrick, ListChecks, XCircle, Zap, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {  ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { RespondToProposal } from '@/components/RespondToProposal';
import useSyncStore from "@/stores/sync";
import { GenerationOutput } from './taskdetail/GenerationOutput';
import { DiffViewer } from './taskdetail/DiffViewer';
import { TimelineViewer } from './taskdetail/TimelineViewer';
import { SummaryCard } from './taskdetail/SummaryCard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';


const getFailures = (validation: ValidatorResults | undefined) => {
    if (!validation) return [];
    const failures = [];
    if(validation.lint?.failures) failures.push(...validation.lint.failures.map(f => ({...f, type: 'Lint'})));
    if(validation.typecheck?.failures) failures.push(...validation.typecheck.failures.map(f => ({...f, type: 'Typecheck', rule: 'type-error'})));
    if(validation.tests?.failures) failures.push(...validation.tests.failures.map(f => ({file: f.suite, message: f.error, rule: f.test, type: 'Test'})));
    return failures;
}


export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(true);

  // Data from stores
  const { byId: tasksById, reopen: reopenTask, createFollowUp, closeTask } = useTasksStore();
  const proposalsByTaskId = useProposalsStore(s => s.byTaskId);
  const { results: validatorResults, progress: validatorProgress, poll: pollValidators } = useValidatorsStore();
  const contextBundlesById = useContextBundlesStore(s => s.byId);
  const { setReviewTaskId } = useUiStore();
  const { setInstructions } = useInstructionsStore((state) => ({
    setInstructions: state.setInstructions,
  }));

  console.log(proposalsByTaskId)

  // State for multiple, concurrent generation streams
  const [generationOutputs, setGenerationOutputs] = useState<Record<string, string>>({});
  const [_progressHashes, setProgressHashes] = useState<Record<string, string | null>>({});
  const [currentThreadIndexes, setCurrentThreadIndexes] = useState<number[]>([]);
  const setCurrentThreadIndex = (nextIndex: number, threadIndex: number) => {
    setCurrentThreadIndexes((prev) => {
      const newIndexes = [...prev];
      newIndexes[threadIndex] = Math.max(0, Math.min(proposalThreads[threadIndex].length - 1, nextIndex));
      return newIndexes;
    });
  }


  const task = id ? tasksById[id] : undefined;
  const proposalsForTask = useMemo(() =>
    task ? Object.values(proposalsByTaskId[task.id] || {}).sort((a, b) => new Date(b.createdAt ?? Date.now()).getTime() - new Date(a.createdAt ?? Date.now()).getTime()) : []
  , [proposalsByTaskId, task]);

  useEffect(() => {
    if (id) {
      setDataLoading(true);
      Promise.all([
        useTasksStore.getState().loadTask(id),
        useProposalsStore.getState().loadProposalsForTask(id),
      ]).finally(() => {
        setDataLoading(false);
      });
    } else {
      setDataLoading(false);
    }
  }, [id]);

 const proposalThreads = useMemo(() => {
    if (!proposalsForTask || proposalsForTask.length === 0) return [];

    const proposalsById = new Map<string, Proposal>(proposalsForTask.map(p => [p.id, p]));

    // Find the root of the AI chosen proposal thread
    let chosenThreadRootId: string | null = null;
    if (task?.currentProposalId && proposalsById.has(task.currentProposalId)) {
        let currentProposal = proposalsById.get(task.currentProposalId)!;
        while(currentProposal.parentProposalId && proposalsById.has(currentProposal.parentProposalId)) {
            currentProposal = proposalsById.get(currentProposal.parentProposalId)!;
        }
        chosenThreadRootId = currentProposal.id;
    }

    const threads: Proposal[][] = [];
    const rootProposalIds = new Set<string>();

    proposalsForTask.forEach(proposal => {
      if (!proposal.parentProposalId) {
        rootProposalIds.add(proposal.id);
      }
    });

    const sortedRootIds = Array.from(rootProposalIds).sort((a, b) => {
        if (chosenThreadRootId) {
            if (a === chosenThreadRootId) return -1;
            if (b === chosenThreadRootId) return 1;
        }
        const dateA = new Date(proposalsById.get(a)!.createdAt!).getTime();
        const dateB = new Date(proposalsById.get(b)!.createdAt!).getTime();
        return dateB - dateA; // Newest root proposals first
    });

    sortedRootIds.forEach(rootId => {
      const thread: Proposal[] = [];
      let currentProposalId: string | undefined = rootId;
      while(currentProposalId) {
        const currentProposal = proposalsById.get(currentProposalId)!;
        thread.push(currentProposal);
        // Find the next proposal in the chain (the one that has this proposal as a parent)
        currentProposalId = proposalsForTask.find(p => p.parentProposalId === currentProposalId)?.id;
      }
      threads.push(thread);
    });

    return threads;
  }, [proposalsForTask, task?.currentProposalId]);
  const contextBundle = task?.contextBundleId ? contextBundlesById[task.contextBundleId] : undefined;
  const validation = task ? validatorResults.byTask[task.id] : undefined;

  const activeValidationRun = useMemo(() => {
    if (!task) return undefined;
    return Object.values(validatorProgress)
      .filter(p => p.taskId === task.id && p.status === 'running')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
  }, [validatorProgress, task]);

  const yoloEvaluation = useMemo(() => {
    if (!task?.description) return null;
    // Split by the specific markdown header for YOLO evaluations
    const parts = task.description.split('### YOLO Mode Evaluation');
    if (parts.length > 1) {
      // Get the content of the last evaluation
      let content = parts.pop()?.trim();
      if (content) {
        // Clean up leading newlines that might result from the split
        content = content.replace(/^[\n\r]+/, '');

      }
      return content || null;
    }
    return null;
  }, [task?.description]);

  const handleRetry = useCallback(() => {
    if (task?.title) {
      setInstructions(task.title);
      toast.success("Task instructions applied to context builder");
      navigate('/pages/workspace/');
    }
  }, [task?.description, setInstructions, navigate]);


  useEffect(() => {
    let interval: number | undefined;
    if (activeValidationRun?.id) {
      const pollFn = () => {
        const currentRun = useValidatorsStore.getState().progress[activeValidationRun.id];
        if (currentRun?.status !== 'running') {
          clearInterval(interval!);
        } else pollValidators(activeValidationRun.id);
      };
      interval = setInterval(pollFn, 2000);
    }
    return () => {
      if (interval) clearInterval(interval!);
    };
  }, [activeValidationRun, pollValidators]);

  useEffect(() => {
    setHeaderTitle(`Task ${id || ''}`);
  }, [setHeaderTitle, id]);

  useEffect(() => {
    // Poll for snapshot updates when a long-running generation (like YOLO) is in progress.
    if (task?.status === 'generating') {
      const intervalId = setInterval(() => {
        useSyncStore.getState().fetchSnapshot();
      }, 3000); // Poll every 3 seconds

      return () => {
        clearInterval(intervalId);
};
    }
  }, [task?.status]);


  // Polling for generation progress
  useEffect(() => {
    const generatingProposals = proposalsForTask.filter(
      p => task?.status === 'generating' && p.status === 'draft' && p.id
    );

    if (generatingProposals.length === 0) {
      return;
    }

    const intervalIds = new Map<string, number>();

    generatingProposals.forEach(proposal => {
      const proposalId = proposal.id;

      const poll = () => {
        // Use functional update to get the latest hash without dependency issues in setInterval
        setProgressHashes(currentHashes => {
          const lastHash = currentHashes[proposalId];
          let endpoint = `/api/proposals/${proposalId}/progress`;
          if (lastHash) {
            endpoint += `?last_hash=${encodeURIComponent(lastHash)}`;
          }

          http<{ status: string; output: string; result: any; hash: string }>(endpoint)
            .then(data => {
              if (data !== undefined) {
                setGenerationOutputs(prev => ({ ...prev, [proposalId]: data.output }));
                setProgressHashes(prev => ({ ...prev, [proposalId]: data.hash }));

                if (data.status === 'complete' || data.status === 'failed') {
                  const intervalId = intervalIds.get(proposalId);
                  if (intervalId) {
                    clearInterval(intervalId);
                    intervalIds.delete(proposalId);
                  }
                  fetchSnapshot(); // A generation finished, refresh the entire state
                }
              }
            })
            .catch(e => {
              console.error(`Polling generation for ${proposalId} failed:`, e);
              const intervalId = intervalIds.get(proposalId);
              if (intervalId) {
                clearInterval(intervalId);
                intervalIds.delete(proposalId);
              }
            });
          return currentHashes; // Return the state for the functional update
        });
      };

      poll(); // Initial poll
      const intervalId = setInterval(poll, 750) as unknown as number;
      intervalIds.set(proposalId, intervalId);
    });

    return () => {
      intervalIds.forEach(id => clearInterval(id));
    };
  }, [proposalsForTask, task?.status]);

  // Stubbed actions as per instructions
  const handleReopen = () => {
    if (task) {
      reopenTask(task.id);
    }
  };

  const handleClose = () => {
    if (task) {
      closeTask(task.id);
    }
  };

  const handleFollowUp = async () => {
    if (task) {
      await createFollowUp(task.id);
      navigate(`/pages/task-new/?from=task-detail&fromTaskId=${task.id}`);
    }
  };

  if (dataLoading) {
        return (
            <div className="container mx-auto p-4 lg:p-6 space-y-6 animate-pulse">
                <header className="space-y-2">
                    <div className="hidden md:block">
                        <Skeleton className="h-8 w-3/4 rounded-md" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Skeleton className="h-6 w-24 rounded-md" />
                        <Skeleton className="h-4 w-48 rounded-md" />
                        <Skeleton className="h-4 w-48 rounded-md" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Skeleton className="h-6 w-20 rounded-md" />
                        <Skeleton className="h-6 w-24 rounded-md" />
                    </div>
                </header>

                <div className="flex flex-wrap gap-2 border-b pb-4">
                    <Skeleton className="h-10 w-36 rounded-md" />
                    <Skeleton className="h-10 w-40 rounded-md" />
                    <Skeleton className="h-10 w-28 rounded-md" />
                    <Skeleton className="h-10 w-28 rounded-md" />
                </div>

                <Tabs defaultValue="proposals" className="w-full">
                    <TabsList className="flex w-full flex-wrap justify-start gap-2 md:justify-center">
                        <Skeleton className="h-9 w-full min-w-[140px] rounded-md sm:w-[140px]" />
                        <Skeleton className="h-9 w-full min-w-[140px] rounded-md sm:w-[140px]" />
                        <Skeleton className="h-9 w-full min-w-[140px] rounded-md sm:w-[140px]" />
                        <Skeleton className="h-9 w-full min-w-[140px] rounded-md sm:w-[140px]" />
                        <Skeleton className="h-9 w-full min-w-[140px] rounded-md sm:w-[140px]" />
                    </TabsList>
                </Tabs>

            </div>
        );
    }

    if (!task) {
        return (
            <div className="container mx-auto p-4 text-center">
                <h1 className="text-xl font-semibold">Task Not Found</h1>
                <p className="text-muted-foreground">
                    The requested task could not be found.
                </p>
                <Button className="mt-4" onClick={() => navigate('/pages/tasks/')}>Go to Tasks</Button>
            </div>
        );
    }

  const { title, status, labels, createdAt, updatedAt, timeline } = task;

  const isTerminated = ['closed', 'merged', 'failed'].includes(status);

  const responsiveButtonClass = "w-full justify-start sm:w-auto sm:justify-center";
  const getActionButtonClass = (isDisabled: boolean) =>
    [responsiveButtonClass, isDisabled ? 'hidden sm:inline-flex' : ''].filter(Boolean).join(' ');

  const canViewValidators =
    proposalsForTask.length > 0 ||
    status === 'validating' ||
    status === 'validated';
  const isValidatorsDisabled = !canViewValidators;
  const isReopenDisabled = !isTerminated;

  return (
    <div className={`container mx-auto space-y-6 ${isMobile ? 'px-0' : 'px-4 py-4 md:px-6 md:py-6'} overflow-visible`}>
      {/* Header Strip */}
      <header className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <h1 className="text-xl font-semibold leading-tight truncate sm:text-2xl sm:font-bold">
            {title || `Task ${id}`}
          </h1>
          <Badge
            variant={status === 'merged' || status === 'complete' ? 'default' : 'secondary'}
            className="w-fit self-start sm:self-center"
          >
            {status}
          </Badge>
        </div>
        <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center">
          <span>Created: {new Date(createdAt).toLocaleString()}</span>
          <span className="hidden sm:inline">•</span>
          <span>Updated: {new Date(updatedAt).toLocaleString()}</span>
        </div>
        {labels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {labels.map(label => (
              <Badge key={label} variant="outline" className="w-fit">
                {label}
              </Badge>
            ))}
          </div>
        )}
      </header>

      {/* Primary Actions */}
      <div className="flex flex-col gap-2 border-b pb-4 sm:flex-row sm:flex-wrap">
        {!isTerminated && (
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/pages/validators/?taskId=${task.id}`)}
              disabled={isValidatorsDisabled}
              className={getActionButtonClass(isValidatorsDisabled)}
            >
              <AlertCircle className="mr-2" /> View Validators
            </Button>
            <Button variant="outline" onClick={handleClose} className={responsiveButtonClass}>
              <XCircle className="mr-2" /> Close Task
            </Button>
          </>
        )}
        {/* Show Close button for failed tasks */}
        {task.status === 'failed' && (
          <Button variant="outline" onClick={handleClose} className={responsiveButtonClass}>
            <XCircle className="mr-2" /> Close Task
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={handleReopen}
          disabled={isReopenDisabled}
          className={getActionButtonClass(isReopenDisabled)}
        >
          <RotateCcw className="mr-2" /> Reopen
        </Button>
        <Button variant="ghost" onClick={handleFollowUp} className={responsiveButtonClass}>
          <Milestone className="mr-2" /> Follow-up Task
        </Button>
        {task?.title !== 'Task from clipboard' && (
          <Button 
            variant="ghost" 
            onClick={handleRetry} 
            className={responsiveButtonClass}
          >
            <RotateCcw className="mr-2" /> Retry
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="proposals" className="w-full">
        <TabsList className="w-full flex-wrap justify-start gap-2 md:justify-center">
          <TabsTrigger value="proposals" className="flex-1 min-w-[140px] sm:flex-none" disabled={proposalsForTask.length === 0}>
            Proposals
          </TabsTrigger>
          <TabsTrigger value="prompt" className="flex-1 min-w-[140px] sm:flex-none">
            Prompt
          </TabsTrigger>
          <TabsTrigger value="context" className="flex-1 min-w-[140px] sm:flex-none">
            Context
          </TabsTrigger>
          <TabsTrigger value="validators" className="flex-1 min-w-[140px] sm:flex-none">
            Validators
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex-1 min-w-[140px] sm:flex-none">
            Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className={isMobile ? 'mt-0' : "mt-4 space-y-6"}>
          {yoloEvaluation && (
            <Card className="border-amber-500/50 dark:border-amber-400/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Zap className="h-5 w-5"/>
                  YOLO Mode Evaluation
                </CardTitle>
                <CardDescription>
                  An AI evaluator reviewed multiple proposals and selected the best one. Its analysis is below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px] w-full border rounded-md p-4 bg-muted/50 font-sans text-sm">
                  <pre className="whitespace-pre-wrap">{yoloEvaluation}</pre>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
          {proposalThreads.length === 0 && (
            <p className="text-sm text-muted-foreground text-center pt-4">No proposals have been generated for this task yet.</p>
          )}


          {proposalThreads.map((currentThread, currentThreadIndex) => {
            return (<div className="space-y-4">
              {(() => {
                const proposalIndex = currentThreadIndexes[currentThreadIndex] ?? currentThread.length - 1;
                const latestProposal = currentThread[proposalIndex] ?? currentThread[currentThread.length - 1];
                const isGenerating = task.status === 'generating' && latestProposal.status === 'draft';

                const isYoloChosen = task.currentProposalId && task.currentProposalId === latestProposal.id && !!yoloEvaluation;

                return (
                  <Card key={latestProposal.id} className={cn(isYoloChosen && "border-2 border-primary shadow-lg")}>
                    <CardHeader>
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                          <div className="flex-1">
                            <CardTitle className="text-lg">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{latestProposal.title || `Proposal #${latestProposal.id}`}</span>
                                {isYoloChosen && (
                                  <Badge>
                                    <Zap className="h-3 w-3 mr-1.5" />
                                    Chosen by AI
                                  </Badge>
                                )}
                              </div>
                            </CardTitle>
                            <CardDescription className="text-xs mt-1">Created at {new Date(latestProposal.createdAt!).toLocaleString()} by {latestProposal.generatingProvider}</CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setCurrentThreadIndex(proposalIndex - 1, currentThreadIndex)} disabled={proposalIndex === 0}> {'<'} </Button>
                            <span className="text-sm text-muted-foreground">{proposalIndex + 1} / {currentThread.length}</span>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setCurrentThreadIndex(proposalIndex + 1, currentThreadIndex)} disabled={proposalIndex >= currentThread.length - 1}> {'>'} </Button>
                          </div>
                        </div>
                        {!isGenerating && (
                          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            <RespondToProposal taskId={task.id} proposalId={latestProposal.id} className="w-full sm:w-auto" />
                            <Button size="sm" disabled={latestProposal.status === 'draft'} className="w-full sm:w-auto" onClick={() => { setReviewTaskId(task.id); navigate(`/pages/review/?taskId=${task.id}&proposalId=${latestProposal.id}`); }}>
                              <CheckCircle className="mr-2 h-4 w-4" /> Review
                            </Button>
                          </div>
                        )}
                        {isGenerating && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Generating...</span>
                              {isGenerating && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span>Generating...</span>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    {latestProposal.userFeedback && <CardContent className="pt-0"><p className="text-sm italic text-muted-foreground">Feedback: "{latestProposal.userFeedback}"</p></CardContent>}
                    {latestProposal.summary && <CardContent className="pt-0"><p className="text-sm">{latestProposal.summary}</p></CardContent>}

                    {(() => {
                      const currentOutput = generationOutputs[latestProposal.id];
                      return isGenerating && typeof currentOutput === 'string' ? (
                        <CardContent className={isMobile ? 'p-2' : 'p-6'}>
                          <GenerationOutput output={currentOutput} proposalId={latestProposal.id} taskId={task.id} />
                        </CardContent>
                      ) : null;
                    })()}

                    <CardContent className={isMobile ? 'p-0' : ''}>
                      {!isGenerating && <Separator className={`mb-4 ${isMobile ? 'hidden' : ''}`} />}
                      <Tabs defaultValue="diff" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="diff">File Changes</TabsTrigger>
                          <TabsTrigger value="raw-output" disabled={!latestProposal.rawProviderResponse}>Raw Output</TabsTrigger>
                        </TabsList>
                        <TabsContent value="diff" className="mt-4">
                          <DiffViewer files={latestProposal.filesChanged} proposal={latestProposal} key={latestProposal.id} />
                        </TabsContent>
                        <TabsContent value="raw-output" className="mt-4">
                          {latestProposal.rawProviderResponse ? (
                            <ScrollArea className="h-[600px] w-full rounded-md border p-4">
                              <div className="prose dark:prose-invert max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{latestProposal.rawProviderResponse}</ReactMarkdown>
                              </div>
                            </ScrollArea>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center pt-4">No raw output available for this proposal.</p>
                          )}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
            );
          })}
        </TabsContent>

        <TabsContent value="context" className="mt-4">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ToyBrick className="h-5 w-5"/>Context Bundle</CardTitle>
                    <CardDescription>The files and symbols used for generation.</CardDescription>
                </CardHeader>
                <CardContent>
                    {contextBundle ? (
                        <>
                            <p>Tokens Used: {contextBundle.tokensUsed} / {contextBundle.budget.hard}</p>
                            <ul className="list-disc pl-5 mt-2 text-sm space-y-1">
                                {contextBundle.items.map(item => (
                                    <li key={item.id} className="font-mono">{item.type}: {item.ref}</li>
                                ))}
                            </ul>
                        </>
                    ) : <p className="text-sm text-muted-foreground">No context bundle found.</p>}
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="validators" className="mt-4">
            <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5"/>Validator Results</CardTitle>
                  <CardDescription>
                    A summary of the latest validation run for this task.
                    <Button variant="link" className="p-1 h-auto" onClick={() => navigate(`/pages/validators/?taskId=${task.id}`)}>View full report</Button>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                    {validation ? (
                      <div className="grid gap-4 md:grid-cols-3">
                        <SummaryCard title="Lint" results={validation} category="lint" />
                        <SummaryCard title="Typecheck" results={validation} category="typecheck" />
                        <SummaryCard title="Tests" results={validation} category="tests" />
                        {getFailures(validation).length > 0 && (
                            <div className="md:col-span-3">
                                <h3 className="text-lg font-semibold mt-4 mb-2">Top Failures</h3>
                                <ul className="space-y-2">
                                    {getFailures(validation).slice(0, 5).map((f, i) => (
                                        <li key={i} className="text-sm p-2 bg-muted rounded-md font-mono truncate"><Badge variant="destructive" className="mr-2">{f.type}</Badge> {f.file}: {f.message}</li>
                                    ))}
                                </ul>
                            </div>)}
                      </div>
                    ) : <p className="text-sm text-muted-foreground">No validation results available.</p>}
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
            <TimelineViewer timeline={timeline} />
        </TabsContent>

      </Tabs>
    </div>
  );
}